import * as React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
  /**
   * `progress` spins a ring, `success` fills the circle with a check, `error`
   * fills it in red with a cross.
   */
  variant: 'progress' | 'success' | 'error';
  title: string;
  body?: React.ReactNode;
  /**
   * Sits inside the ring. The waiting screens on Zadání kurzu and Výstupy draw
   * a clipboard and a document there; the transfer states draw nothing.
   */
  icon?: React.ReactNode;
};

const CIRCLE = 100;
const RING = 5;

/**
 * The circle-heading-copy block that opens a Klara screen.
 *
 * One component for what the design draws five times: the two waiting screens,
 * "Hotovo!" on completed Výstupy, and both transfer states. They differ only in
 * the circle, so the rest is shared rather than pasted.
 */
export function StatusHeader({ variant, title, body, icon }: Props) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px 24px 32px',
      }}
    >
      {variant === 'progress' && <ProgressCircle icon={icon} />}
      {variant === 'success' && (
        <FilledCircle
          color="var(--color-primary)"
          halo="var(--color-primary15)"
          icon={<CheckIcon sx={{ fontSize: '56px' }} />}
        />
      )}
      {variant === 'error' && (
        <FilledCircle
          color="var(--color-error)"
          halo="var(--color-error-back)"
          icon={<CloseIcon sx={{ fontSize: '56px' }} />}
        />
      )}

      <Typography
        sx={{
          fontSize: '30px',
          fontWeight: 600,
          color: 'var(--color-black)',
          marginTop: '12px',
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>

      {body && (
        <Typography
          sx={{
            fontSize: '16px',
            color: 'var(--color-black-text)',
            marginTop: '16px',
            maxWidth: '860px',
            textAlign: 'center',
          }}
        >
          {body}
        </Typography>
      )}
    </Box>
  );
}

function ProgressCircle({ icon }: { icon?: React.ReactNode }) {
  return (
    <Box sx={{ position: 'relative', width: CIRCLE, height: CIRCLE }}>
      {/* MUI measures thickness against a 44-unit viewBox, so the stroke lands
          at thickness * size / 44 — 2.2 gives the 5px the design draws. */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={CIRCLE}
        thickness={(RING * 44) / CIRCLE}
        sx={{ color: '#e0e0e0', position: 'absolute', left: 0 }}
      />
      <CircularProgress
        size={CIRCLE}
        thickness={(RING * 44) / CIRCLE}
        sx={{ color: 'var(--color-primary)', position: 'absolute', left: 0 }}
      />
      {icon && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // The grey the design draws these in, not the heading black.
            color: 'var(--color-black-text)',
          }}
        >
          {icon}
        </Box>
      )}
    </Box>
  );
}

function FilledCircle({
  color,
  halo,
  icon,
}: {
  color: string;
  halo: string;
  icon: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        width: CIRCLE,
        height: CIRCLE,
        borderRadius: '50%',
        backgroundColor: color,
        color: 'var(--color-white)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // The halo the design puts behind the circle, in the tint that goes
        // with its fill — the space's own colour on the success state.
        boxShadow: `0 0 24px 0 ${halo}`,
      }}
    >
      {icon}
    </Box>
  );
}
